import { useEffect, useState } from "react";
import api from "@/lib/api";

const STORAGE_KEY = "rs_age_verified";
// We use sessionStorage (not localStorage) on purpose — the age gate should
// re-appear on every new browser session (compliance best practice for adult
// commerce). Refresh + in-tab navigation still won't re-prompt.
const ageStore = typeof window !== "undefined" ? window.sessionStorage : null;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const AgeGate = () => {
  const [verified, setVerified] = useState(true);
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  // "revealing" plays a 2s Santorini fade-out over the Home splash the moment
  // the gate is cleared — a cinematic threshold before the black logo lands.
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    const v = ageStore?.getItem(STORAGE_KEY);
    if (v !== "1") setVerified(false);
    // Geo-block check runs regardless (compliance)
    api
      .get("/geo/check")
      .then((r) => {
        if (!r.data.allowed) {
          setGeoBlocked(true);
          setVerified(false);
          ageStore?.removeItem(STORAGE_KEY);
        }
      })
      .catch(() => {});
  }, []);

  if (verified && !geoBlocked) return null;

  // Entrance reveal — plain Santorini plate fading to transparent over 2s so
  // the black Home splash beneath it emerges gracefully.
  if (revealing) {
    return (
      <div
        data-testid="age-gate-entrance-reveal"
        aria-hidden="true"
        className="fixed inset-0 z-[100] pointer-events-none rs-entrance-reveal"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/6b746f73-141d-4e23-a891-20657f4c6e33/images/e809a07e3103e1da470d21e6d553e04bc3fa58edd64c7c19e6c9da9bf83f0f0f.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <style>{`
          @keyframes rs-entrance-fade {
            0%   { opacity: 1; }
            40%  { opacity: 1; }
            100% { opacity: 0; }
          }
          .rs-entrance-reveal { animation: rs-entrance-fade 2000ms ease-out forwards; }
        `}</style>
      </div>
    );
  }

  const nowYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => nowYear - i);
  const daysInMonth = month && year ? new Date(Number(year), Number(month), 0).getDate() : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const submit = async () => {
    setError("");
    if (!month || !day || !year) {
      setError("Please enter your full date of birth.");
      return;
    }
    if (!agree) {
      setError("You must confirm the statement below to enter.");
      return;
    }
    const dob = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setChecking(true);
    try {
      await api.post("/age/verify", { dob });
      ageStore?.setItem(STORAGE_KEY, "1");
      // Start the entrance reveal: swap the gate UI for a Santorini plate that
      // fades out over ~2s, then unmount so the black Home splash settles in.
      setRevealing(true);
      window.setTimeout(() => setVerified(true), 2000);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 451) {
        setGeoBlocked(true);
      } else {
        setError(e?.response?.data?.detail || "Verification failed. Please try again.");
      }
    } finally {
      setChecking(false);
    }
  };

  const reject = () => {
    window.location.href = "https://www.google.com";
  };

  const selectCls =
    "bg-transparent border px-3 py-3 text-sm text-bone outline-none appearance-none cursor-pointer";
  const selectStyle = { borderColor: "rgba(245,242,235,0.25)", background: "rgba(10,10,10,0.7)" };

  return (
    <div
      data-testid="age-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto"
      style={{
        backgroundImage:
          "linear-gradient(rgba(10,10,10,0.78), rgba(10,10,10,0.92)), url('https://static.prod-images.emergentagent.com/jobs/6b746f73-141d-4e23-a891-20657f4c6e33/images/e809a07e3103e1da470d21e6d553e04bc3fa58edd64c7c19e6c9da9bf83f0f0f.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backdropFilter: "blur(4px)",
      }}
    >
      {geoBlocked ? (
        <div className="max-w-xl mx-6 text-center fade-up py-12" data-testid="geo-blocked-screen">
          <div className="overline mb-6">Ram Shack — Est. 2026</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-bone mb-6 leading-tight">
            Not available in your region.
          </h1>
          <p className="text-muted-bone text-base sm:text-lg max-w-lg mx-auto mb-10 leading-relaxed">
            Due to laws governing the sale and import of adult novelty products,
            we are unable to serve visitors from your country. We apologise —
            the house must obey the law of the land.
          </p>
          <button
            data-testid="geo-blocked-leave-btn"
            onClick={reject}
            className="text-muted-bone hover:text-bone px-10 py-4 uppercase text-xs tracking-[0.22em] transition border"
            style={{ borderColor: "rgba(245,242,235,0.25)" }}
          >
            Leave
          </button>
        </div>
      ) : (
        <div className="max-w-xl mx-6 text-center fade-up py-12">
          <div className="overline mb-6">Ram Shack — Est. 2026</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-bone mb-6 leading-tight">
            You must be of age to enter.
          </h1>
          <p className="text-xs uppercase tracking-[0.24em] mb-4" style={{ color: "var(--rs-ruby, #c05050)" }} data-testid="age-gate-warning-label">
            Warning — Sexually Oriented Material
          </p>
          <p className="text-muted-bone text-base sm:text-lg max-w-lg mx-auto mb-8 leading-relaxed" data-testid="age-gate-warning-text">
            This website contains sexually oriented adult products and graphics
            intended for adults only. You must be at least{" "}
            <span className="text-bone">18 years of age</span> — or{" "}
            <span className="text-bone">21</span> where that is the applicable
            minimum legal age in your jurisdiction — to enter. Please verify
            your date of birth.
          </p>

          <div className="flex justify-center gap-3 mb-6">
            <select
              data-testid="age-gate-month-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={selectCls}
              style={selectStyle}
              aria-label="Birth month"
            >
              <option value="">Month</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              data-testid="age-gate-day-select"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className={selectCls}
              style={selectStyle}
              aria-label="Birth day"
            >
              <option value="">Day</option>
              {days.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <select
              data-testid="age-gate-year-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={selectCls}
              style={selectStyle}
              aria-label="Birth year"
            >
              <option value="">Year</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <label className="flex items-start gap-3 max-w-lg mx-auto mb-6 text-left cursor-pointer">
            <input
              data-testid="age-gate-agree-checkbox"
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-1"
            />
            <span className="text-muted-bone text-sm leading-relaxed">
              I certify under penalty of law that I am at least 18 years of age
              (or the applicable minimum legal age in my jurisdiction), that
              sexually oriented material does not offend me, and that I am not
              accessing this site from a jurisdiction where such material is
              prohibited.
            </span>
          </label>

          {error && (
            <p data-testid="age-gate-error" className="text-sm mb-6" style={{ color: "var(--rs-ruby, #c05050)" }}>
              {error}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              data-testid="age-gate-confirm-btn"
              onClick={submit}
              disabled={checking}
              className="bg-oxblood text-bone px-10 py-4 uppercase text-xs tracking-[0.22em] hover:opacity-90 transition w-full sm:w-auto disabled:opacity-50"
              style={{ background: "var(--rs-oxblood)" }}
            >
              {checking ? "Verifying…" : "Verify & Enter"}
            </button>
            <button
              data-testid="age-gate-decline-btn"
              onClick={reject}
              className="text-muted-bone hover:text-bone px-10 py-4 uppercase text-xs tracking-[0.22em] transition"
            >
              Leave immediately
            </button>
          </div>
          <p className="mt-8 text-xs tracking-[0.18em] uppercase text-muted-bone/70">
            Discreet shipping · Plain packaging · Encrypted checkout
          </p>
        </div>
      )}
    </div>
  );
};

export default AgeGate;
